import type { ApiTimeWindow, SeriesRow, ChartPoint, TimeWindow } from '@/views/deepDiveView/types'

/** Map a UI named window to an API window. Custom ranges use TimeQuery.start/end directly. */
export const toApiWindow = (w: TimeWindow): ApiTimeWindow =>
  w === 'custom' ? '1h' : w

/** Window length in milliseconds. `Infinity` means unbounded. */
export const windowLengthMs = (w: ApiTimeWindow): number => {
  switch (w) {
    case '1h':  return 3_600_000
    case '12h': return 43_200_000
    case '24h': return 86_400_000
    case 'all': return Infinity
  }
}

/**
 * Compute the absolute [from, to] range to plot.
 *
 * `to` is the historic anchor in historic mode, otherwise `nowTs`. If we're
 * in historic mode but no anchor is set, fall back to the latest row in the
 * series (or `nowTs` if empty) — preserves the original behaviour.
 */
export const windowRange = (opts: {
  windowMs: number
  timeMode: 'live' | 'historic'
  historicTs: number | null | undefined
  historicFromTs?: number | null | undefined
  nowTs: number
  rows: ReadonlyArray<SeriesRow>
}): { fromMs: number; toMs: number } => {
  const { windowMs, timeMode, historicTs, historicFromTs, nowTs, rows } = opts
  if (
    timeMode === 'historic' &&
    typeof historicFromTs === 'number' &&
    typeof historicTs === 'number'
  ) {
    return {
      fromMs: Math.min(historicFromTs, historicTs),
      toMs: Math.max(historicFromTs, historicTs),
    }
  }
  const lastRowTs = rows.length ? Number(rows[rows.length - 1]?.tsMs) : NaN
  const fallback = Number.isFinite(lastRowTs) ? lastRowTs : nowTs

  const toMs = timeMode === 'historic' ? (historicTs ?? fallback) : nowTs
  const fromMs = windowMs === Infinity ? Number.NEGATIVE_INFINITY : toMs - windowMs
  return { fromMs, toMs }
}

const num = (v: unknown): number | null => {
  const n = Number(v)
  return Number.isFinite(n) ? n : null
}

/** Convert raw API rows into chart-ready points, filtering to the window. */
export const toChartPoints = (
  rows: ReadonlyArray<SeriesRow>,
  fromMs: number,
  toMs: number,
): ChartPoint[] => {
  const out: ChartPoint[] = []
  for (const r of rows) {
    const ts = Number(r.tsMs)
    if (!Number.isFinite(ts) || ts < fromMs || ts > toMs) continue
    const pt: ChartPoint = { ts }
    for (const k of Object.keys(r)) {
      if (k === 'tsMs') continue
      const raw = (r as Record<string, unknown>)[k]
      // Preserve null (esp. *_tier) — Number(null) === 0 would fake critical.
      pt[k] = raw == null ? null : num(raw)
    }
    out.push(pt)
  }
  out.sort((a, b) => a.ts - b.ts)
  return out
}

export const fmtTime = (ts: number): string =>
  new Date(ts).toLocaleTimeString('en-GB')

export const stableStringify = (obj: unknown): string => {
  const seen = new WeakSet<object>()
  const norm = (v: unknown): unknown => {
    if (!v || typeof v !== 'object') return v
    if (seen.has(v as object)) return null
    seen.add(v as object)
    if (Array.isArray(v)) return v.map(norm)
    const src = v as Record<string, unknown>
    const out: Record<string, unknown> = {}
    for (const k of Object.keys(src).sort()) out[k] = norm(src[k])
    return out
  }
  return JSON.stringify(norm(obj))
}
